import { client } from '@/client';
import {
  AccountVariable,
  AccountVariableType,
  SaveAccountVariableRequest,
} from '@/client/accounts';
import { MessageResponseModel } from '@/client/models';
import { GenericForbiddenApiError } from '@/hooks/query/types';
import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const queryKey = 'account-variables';

const ACCOUNT_VARIABLES_STALE_TIME = 5 * 60 * 1000;
const ACCOUNT_VARIABLES_CACHE_TIME = 30 * 60 * 1000;

export const usePrefetchAccountVariables = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (accountId?: string) => {
      if (!accountId) return;

      queryClient.prefetchQuery(
        [queryKey, accountId],
        () => client.accountVariables.getAccountVariables(accountId),
        {
          staleTime: ACCOUNT_VARIABLES_STALE_TIME,
          cacheTime: ACCOUNT_VARIABLES_CACHE_TIME,
        },
      );
    },
    [queryClient],
  );
};

export const useAccountVariables = (
  accountId?: string,
  params?: UseQueryOptions,
): UseQueryResult<AccountVariable[]> & {
  variables: AccountVariable[] | undefined;
  itContactConfigured: boolean;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = ACCOUNT_VARIABLES_STALE_TIME,
    cacheTime = ACCOUNT_VARIABLES_CACHE_TIME,
    ...restOptions
  } = params || {};

  const { data, ...rest } = useQuery(
    [queryKey, accountId],
    () => client.accountVariables.getAccountVariables(accountId as string),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
      enabled: !!accountId && (restOptions.enabled ?? true),
    },
  );

  return {
    variables: data,
    itContactConfigured: !!data?.some(
      (variable) =>
        variable.type === AccountVariableType.IT_CONTACT &&
        variable.value?.name?.trim(),
    ),
    ...(rest as UseQueryResult<AccountVariable[]>),
  };
};

export const useSaveAccountVariable = (
  accountId: string,
): {
  saveVariable: (input: {
    type: AccountVariableType;
    payload: SaveAccountVariableRequest;
  }) => Promise<AccountVariable>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    AccountVariable,
    GenericForbiddenApiError,
    { type: AccountVariableType; payload: SaveAccountVariableRequest }
  >(
    'saveAccountVariable',
    ({ type, payload }) =>
      client.accountVariables.saveAccountVariable(accountId, type, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return {
    saveVariable: mutateAsync,
    ...(rest as any),
  };
};

export const useDeleteAccountVariable = (
  accountId: string,
): {
  deleteVariable: (type: AccountVariableType) => Promise<MessageResponseModel>;
  isLoading: boolean;
} => {
  const queryClient = useQueryClient();

  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    AccountVariableType
  >(
    'deleteAccountVariable',
    (type) => client.accountVariables.deleteAccountVariable(accountId, type),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey, accountId]);
      },
    },
  );

  return {
    deleteVariable: mutateAsync,
    ...(rest as any),
  };
};
